import { useCallback, useEffect, useState } from 'react'

type DialogPosition = {
  x: number
  y: number
  width?: number
  height?: number
}

type UseInquiryDialogPositionProps = {
  defaultPosition: DialogPosition
  minWidth?: number
  minHeight?: number
  padding?: number
  bottomOffset?: number
}

export const useInquiryDialogPosition = ({
  defaultPosition,
  minWidth = 0,
  minHeight = 0,
  padding = 8,
  bottomOffset = 0,
}: UseInquiryDialogPositionProps) => {
  const [position, setPosition] = useState<DialogPosition>(defaultPosition)

  // 画面のリサイズ時に位置を再計算する
  const handleResize = useCallback(() => {
    setPosition(prev => {
      const maxWidth = window.innerWidth - padding * 2
      const maxHeight = window.innerHeight - padding * 2

      const width = prev.width
        ? Math.min(Math.max(minWidth, prev.width), maxWidth)
        : undefined
      const height = prev.height
        ? Math.min(Math.max(minHeight, prev.height), maxHeight)
        : undefined

      const maxX = window.innerWidth - (width ?? minWidth) - padding
      const maxY =
        window.innerHeight - (height ?? minHeight) - padding - bottomOffset

      return {
        x: Math.min(Math.max(padding, prev.x), maxX),
        y: Math.min(Math.max(0, prev.y), maxY),
        width,
        height,
      }
    })
  }, [minWidth, minHeight, padding, bottomOffset])

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [handleResize])

  const onChangePosition = useCallback((position: DialogPosition) => {
    setPosition(prev => ({ ...prev, ...position }))
  }, [])

  return {
    position,
    onChangePosition,
  }
}
